import React, { useEffect, useRef, useState } from 'react';

import { cancelarChegada } from '../../../api/previsaoPecas/previsaoPecasApi';
import { Chegada, DetalheDoPedido } from '../../../interfaces/PrevisaoPecas';
import { notify } from '../../../shared/feedback/notify';
import BarraDeChegada from './BarraDeChegada';
import { STATUS_DA_PREVISAO, formatarDiaPuro, formatarInstante, formatarQuantidade, rotuloDoStatus } from '../formatos';
// Overlay compartilhado e a receita do "modal de ações por linha" (seções, grade, ações).
import '../../../components/modal/add-car/AddCarModal.css';
import '../../portal_cotacoes/components/ConviteAcoesModal.css';
import '../../admin-sistema/pages/css/adminSistema.css'; // campo do motivo e botões
import '../../../shared/ui/dashboard-base.css'; // `do-celula-link`
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';

const MOTIVO_MIN = 3;

type ItemComChegadas = DetalheDoPedido['itens'][number];

interface Props {
  item: ItemComChegadas;
  pedidoNumero: string | null;
  podeConferir: boolean;
  /** As ações abaixo FECHAM este modal antes de abrir outro (o filho ficaria fora do `ref` deste). */
  onRegistrarChegada: () => void;
  onAbrirNota: (notaId: string) => void;
  /** Uma chegada foi cancelada aqui dentro: a tela do pedido recarrega. */
  onCancelado: () => Promise<void>;
  onFechar: () => void;
}

/**
 * Uma peça do pedido (previsao-pecas/at-02): quanto chegou, cada chegada com QUEM a registrou — o
 * SOAC, pela nota de entrada, ou uma pessoa — e as ações. Aberta pela tabela de peças da tela do
 * pedido (pedido do Victor, 24/10/2026: modal, não tabela nova).
 *
 * O cancelamento mora DENTRO deste modal (não num filho), no molde do antigo
 * `RecebimentoDetalheModal` do Estoque: motivo obrigatório, linha não apagada, saldo volta sozinho.
 */
const PecaDoPedidoModal: React.FC<Props> = ({
  item,
  pedidoNumero,
  podeConferir,
  onRegistrarChegada,
  onAbrirNota,
  onCancelado,
  onFechar,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [cancelando, setCancelando] = useState<Chegada | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erroDoMotivo, setErroDoMotivo] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const unidade = item.unidade ?? 'un';

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar]);

  const confirmarCancelamento = async () => {
    if (!cancelando) return;
    if (motivo.trim().length < MOTIVO_MIN) {
      setErroDoMotivo('Escreva o motivo do cancelamento — ele fica no histórico no lugar do registro.');
      return;
    }
    setSalvando(true);
    try {
      await cancelarChegada(cancelando.id, motivo.trim());
    } catch (error) {
      // O formulário continua aberto: fechar anunciaria um cancelamento que não aconteceu.
      notify.actionError(error, { action: 'delete', subject: 'registro de chegada' });
      setSalvando(false);
      return;
    }
    setSalvando(false);
    setCancelando(null);
    setMotivo('');
    setErroDoMotivo(null);
    notify.success('Registro de chegada cancelado. A peça volta a contar como pendente.');
    await onCancelado();
  };

  const descricao = item.produtoDescricao ?? 'Peça sem descrição';

  return (
    <div className="modal-overlay">
      <div className="convite-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Chegadas da peça">
        <div className="convite-modal-header">
          <div className="convite-modal-titulo">
            <h2 title={descricao}>{descricao}</h2>
            {pedidoNumero && <span className="convite-modal-cnpj">- Pedido {pedidoNumero}</span>}
          </div>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="convite-modal-corpo">
          <section className="convite-modal-secao">
            <h3>Peça</h3>
            <div className="convite-modal-grade">
              <span className="convite-modal-rotulo">Código</span>
              <span>{item.produtoCodigo ?? 'não identificado'}</span>

              <span className="convite-modal-rotulo">Chegada</span>
              <span>
                <BarraDeChegada recebida={item.chegada} pedida={item.pedida} unidade={unidade} largura={90} />
              </span>

              {item.excedente > 0 && (
                <>
                  <span className="convite-modal-rotulo">Acima do pedido</span>
                  <span>
                    {formatarQuantidade(item.excedente)} {unidade}
                  </span>
                </>
              )}

              <span className="convite-modal-rotulo">Situação</span>
              <span>
                <span className={`cilia-badge ${STATUS_DA_PREVISAO[item.status].classe}`}>
                  {rotuloDoStatus(item.status)}
                </span>
              </span>

              <span className="convite-modal-rotulo">Previsão</span>
              <span>
                {formatarDiaPuro(item.previsao)}
              </span>
            </div>
          </section>

          <section className="convite-modal-secao">
            <h3>Chegadas</h3>
            {item.chegadas.length === 0 ? (
              <p className="convite-modal-vazio">Nenhuma chegada registrada para esta peça.</p>
            ) : (
              <ul className="convite-modal-envios">
                {item.chegadas.map((c) => (
                  <li key={c.id} className="convite-modal-envio">
                    <span className="convite-modal-envio-canal" style={c.cancelada ? { textDecoration: 'line-through' } : undefined}>
                      {formatarQuantidade(c.quantidade)} {unidade}
                    </span>
                    <span className="convite-modal-envio-estado">
                      {c.cancelada ? (
                        <span className="cilia-badge badge-cinza">Cancelado</span>
                      ) : (
                        formatarDiaPuro(c.chegouEm)
                      )}
                    </span>
                    <span className="convite-modal-envio-quando">
                      {c.origem === 'NOTA' && c.notaId ? (
                        <>
                          SOAC, pela nota{' '}
                          <button
                            type="button"
                            className="do-celula-link"
                            onClick={() => onAbrirNota(c.notaId as string)}
                          >
                            {c.notaNumero ?? 'sem número'}
                          </button>
                        </>
                      ) : (
                        <>
                          {c.registradoPor}, em {formatarInstante(c.registradoEm)}
                        </>
                      )}
                      {c.distribuicao === 'EXCEDENTE' ? ' · acima do pedido' : ''}
                    </span>
                    {c.cancelada && (
                      <span className="convite-modal-envio-motivo">
                        Cancelado por {c.canceladoPorNome ?? 'não informado'}
                        {c.canceladoMotivo ? `: ${c.canceladoMotivo}` : ''}
                      </span>
                    )}
                    {podeConferir && !c.cancelada && cancelando?.id !== c.id && (
                      <span className="convite-modal-envio-motivo">
                        <button
                          type="button"
                          className="do-celula-link"
                          onClick={() => {
                            setCancelando(c);
                            setMotivo('');
                            setErroDoMotivo(null);
                          }}
                        >
                          Cancelar registro
                        </button>
                      </span>
                    )}
                    {cancelando?.id === c.id && (
                      <div className="convite-modal-envio-motivo">
                        <div className="admin-input-group" style={{ marginTop: 4 }}>
                          <label className="admin-label" htmlFor="motivo-cancelamento">
                            Motivo do cancelamento
                          </label>
                          <input
                            id="motivo-cancelamento"
                            className="admin-input"
                            type="text"
                            maxLength={200}
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ex.: a nota foi lançada em duplicidade"
                          />
                          <p className="admin-hint">
                            O registro não é apagado: fica no histórico, riscado, com o motivo. A quantidade volta a
                            contar como pendente.
                          </p>
                          {erroDoMotivo && (
                            <p className="erro-de-tela" role="alert">
                              {erroDoMotivo}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              type="button"
                              className="admin-btn admin-btn-secondary"
                              onClick={() => setCancelando(null)}
                              disabled={salvando}
                            >
                              Voltar
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-primary"
                              onClick={confirmarCancelamento}
                              disabled={salvando}
                            >
                              {salvando ? 'Cancelando...' : 'Confirmar cancelamento'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {podeConferir && (
            <section className="convite-modal-secao">
              <h3>Ações</h3>
              <div className="convite-modal-acoes">
                {podeConferir && (
                  <button type="button" className="convite-modal-acao" onClick={onRegistrarChegada}>
                    <span className="convite-modal-acao-rotulo">Registrar chegada</span>
                    <span className="convite-modal-acao-descricao">
                      Para a peça que chegou antes de a nota entrar no Nasajon. Fica registrada com o seu nome.
                    </span>
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default PecaDoPedidoModal;
